import React, { useState, useMemo, useEffect, useCallback } from 'react';
import Button from '../../../composants/Button/Button';
import CompteARebourd from '../../../composants/CompteARebourd/CompteARebourd';
import ValidationPopUp from '../../../composants/ValidationPopUp/ValidationPopUp';
import OperationFormulaireAdmin from './OperationFormulaireAdmin';
import CGVAccept from '../../../composants/CGVAccept/CGVAccept';
import Coordonnees from '../../../composants/Coordonnees/Coordonnees';
import AnalyseAccordion from '../../../composants/AnalyseAccordion/AnalyseAccordion';
import { ReactComponent as PdfIcon } from '../../../assets/icons/pdf.svg';
import { PrintButton } from '../../../composants/PrintButton/PrintButton';
import SignatureFlow from '../../../composants/Marche/SignatureFlow';
import Banner from '../../../composants/Banner/Banner';
import { useAuth } from '../../../contexts/AuthContext';
import { isSignataireRole } from '../../../utils/roleUtils';
import { formatPrice, getStatusColor } from '../../../utils/entityUtils';
import { useLotImage } from '../../../utils/getLotImage';
import { StatusId, OperationBCI, OperationFormulaire, HistoriqueEvent, Signataire } from '../types';
import { computeOffreActions, operationActionsInternals } from '../domain/operationActions';
import {
  formatOffreArticles,
  totalOffreArticles,
  computeOffreState,
  computeOffreStatusFlags,
} from '../domain/operationOffre';
import {
  accepterOffre,
  refuserOffre,
  annulerOperation,
  updateReferenceInterne,
  loadSignataires,
  notifierResponsable,
  telechargerBonCommande,
  fetchSignatureModeBdc,
} from '../services/operationBciService';
import './OperationOffre.css';

const { isExpired: isOperationExpired } = operationActionsInternals;

interface OperationOffreProps {
  commande: OperationBCI;
  rang: number;
  acceptCGV?: boolean;
  setAcceptCGV?: React.Dispatch<React.SetStateAction<boolean>>;
  printRef?: React.RefObject<HTMLElement>;
}

interface SelectedResponsable {
  id: number;
  type: string;
}

// Onglet Offre acheteur-BC (V2) : decision sur l'offre du prestataire d'un rang.
// Autonome : aucune dependance a l'ancienne feuille composants/OperationOffreBC.
// Logique des boutons -> domain/operationActions ; derivations -> domain/operationOffre ;
// appels API -> services/operationBcService.
const OperationOffre: React.FC<OperationOffreProps> = ({
  commande,
  rang,
  acceptCGV = false,
  setAcceptCGV = () => { },
  printRef,
}) => {
  const { user } = useAuth();
  const isSignataire = isSignataireRole(user?.role?.toString() ?? '');
  const lotImage = useLotImage(
    commande.operation?.lot_technique?.nom || commande.lot_technique?.nom,
  );

  const [operationData, setOperationData] = useState<OperationBCI>(commande);

  // Etats UI
  const [loading, setLoading] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isCGVModalOpen, setIsCGVModalOpen] = useState(false);
  const [readyToAcceptOffreAfterCGV, setReadyToAcceptOffreAfterCGV] = useState(false);
  const [isAcceptOffreModalOpen, setIsAcceptOffreModalOpen] = useState(false);
  const [isRefuseOffreModalOpen, setIsRefuseOffreModalOpen] = useState(false);
  const [isCancelModalOpen, setIsCancelModalOpen] = useState(false);
  // Feedback inline (remplace les alert()) : succes/erreur affiche en banniere en tete.
  const [feedback, setFeedback] = useState<{ type: 'success' | 'danger'; message: string } | null>(null);

  // Notification au responsable signataire
  const [isNotifyResponsableModalOpen, setIsNotifyResponsableModalOpen] = useState(false);
  const [responsables, setResponsables] = useState<Signataire[]>([]);
  const [selectedResponsable, setSelectedResponsable] = useState<SelectedResponsable | null>(null);
  const [responsablesLoading, setResponsablesLoading] = useState(false);

  // Reference interne (panier inverse)
  const [referenceInterne, setReferenceInterne] = useState('');
  const [originalReference, setOriginalReference] = useState('');
  const [referenceInitialized, setReferenceInitialized] = useState(false);
  const [updatingReference, setUpdatingReference] = useState(false);

  // Mode de signature BDC + flow manuscrit
  const [bdcSignatureMode, setBdcSignatureMode] = useState<'automatique' | 'manuscrite'>(() => {
    const cached = localStorage.getItem('bdc_signature_mode');
    return cached === 'manuscrite' || cached === 'automatique' ? cached : 'automatique';
  });
  const [showBdcSignatureModal, setShowBdcSignatureModal] = useState(false);
  const [pendingBdcSignData, setPendingBdcSignData] = useState<{ operationId: number; commandeId: number } | null>(null);

  // Expiration : l'event runtime `chronoExpired` est local, le reste (statut/date)
  // est calcule par le domaine.
  const [chronoExpired, setChronoExpired] = useState(false);

  // La donnee vient de la prop parent (rafraichie). Pas de fetch ni de sniffing d'URL.
  useEffect(() => {
    setOperationData(commande);
  }, [commande]);

  useEffect(() => {
    const handler = () => setChronoExpired(true);
    window.addEventListener('chronoExpired', handler);
    return () => window.removeEventListener('chronoExpired', handler);
  }, []);

  useEffect(() => {
    fetchSignatureModeBdc()
      .then((mode) => {
        if (mode) {
          localStorage.setItem('bdc_signature_mode', mode);
          setBdcSignatureMode(mode);
        }
      })
      .catch(() => { /* garder le mode cache ou le defaut */ });
  }, []);

  useEffect(() => {
    if (!referenceInitialized) {
      const existing =
        operationData.reference || operationData.formulaire?.referenceInterne || '';
      setReferenceInterne(existing);
      setOriginalReference(existing);
      setReferenceInitialized(true);
    }
  }, [operationData, referenceInitialized]);

  const expired = useMemo(
    () => isOperationExpired(operationData, chronoExpired),
    [operationData, chronoExpired],
  );

  const notificationInfo = useMemo(() => {
    const operationId = operationData.id;
    const historique: HistoriqueEvent[] = operationData.historique || [];
    if (!operationId) return { sent: false, labels: [] as string[] };

    const events = historique.filter(
      (event) => event.prefix === 'Notification' && event.type === `BDC.Operation.${operationId}`,
    );
    if (events.length > 0) {
      const labels = events
        .slice()
        .sort((a, b) => new Date(b.created_at || '').getTime() - new Date(a.created_at || '').getTime())
        .map((event) => event.label)
        .filter((label): label is string => label !== null && label !== undefined);
      return { sent: true, labels };
    }
    return { sent: false, labels: [] as string[] };
  }, [operationData]);

  const articles = useMemo(() => formatOffreArticles(operationData, rang), [operationData, rang]);
  const totalOffre = useMemo(() => totalOffreArticles(articles), [articles]);
  const offreState = useMemo(() => computeOffreState(operationData, rang), [operationData, rang]);
  const statusFlags = useMemo(() => computeOffreStatusFlags(operationData, rang), [operationData, rang]);
  const actions = useMemo(
    () => computeOffreActions(operationData, rang, { isSignataire, chronoExpired }),
    [operationData, rang, isSignataire, chronoExpired],
  );

  // Banniere de refus/expiration/annulation, pilotee par l'HISTORIQUE reel (l'evenement de refus
  // est ecrit par le backend, ex. cote acheteur : « Refuse l'offre du prestataire »). On affiche le
  // message de l'evenement + sa date/heure -> plus d'attribution erronee a un prestataire.
  const refusBannerText = useMemo<string | null>(() => {
    if (!offreState.showRefusMessage) return null;
    const info = offreState.refusInfo;
    const refusEvent = (operationData.historique || [])
      .filter((e) => e.prefix === 'Refuse' && (e.type || '').toLowerCase().includes(`operation.${operationData.id}`))
      .sort((a, b) => new Date(b.created_at || '').getTime() - new Date(a.created_at || '').getTime())[0];
    const dateSrc = refusEvent?.created_at || info?.date || '';
    const d = dateSrc ? new Date(dateSrc) : null;
    const dateStr =
      d && !Number.isNaN(d.getTime())
        ? ` le ${d.toLocaleDateString('fr-FR')} à ${d.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}`
        : '';
    if (info?.isCancelled) return `Opération annulée${dateStr}.`;
    if (info?.isExpired) return `Offre expirée${dateStr}.`;
    return `${refusEvent?.message || 'Offre refusée'}${dateStr}.`;
  }, [offreState, operationData]);

  // --- Handlers ---------------------------------------------------------------
  const findCommandeId = useCallback(
    (): number | undefined =>
      operationData.commandes?.find((c) => parseInt(String(c.rang), 10) === rang)?.id,
    [operationData, rang],
  );

  const saveReferenceIfChanged = useCallback(async (): Promise<boolean> => {
    if (referenceInterne === originalReference) return true;
    try {
      setUpdatingReference(true);
      const result = await updateReferenceInterne(operationData.id, referenceInterne.trim());
      if (result?.data) {
        const newRef = referenceInterne.trim();
        setOriginalReference(newRef);
        setOperationData((prev) => ({
          ...prev,
          reference: newRef,
          formulaire: { ...prev.formulaire, referenceInterne: newRef },
        }));
      }
      return true;
    } catch (error) {
      setFeedback({ type: 'danger', message: error instanceof Error ? error.message : 'Erreur lors de la mise à jour de la référence' });
      return false;
    } finally {
      setUpdatingReference(false);
    }
  }, [referenceInterne, originalReference, operationData]);

  // Apres acquisition des CGV : signature (manuscrite) ou popup de confirmation (automatique).
  const openSignatureOrAccept = useCallback(() => {
    if (bdcSignatureMode === 'manuscrite') {
      const commandeId = findCommandeId();
      if (operationData.id && commandeId) {
        setPendingBdcSignData({ operationId: operationData.id, commandeId });
        setShowBdcSignatureModal(true);
      }
    } else {
      setIsAcceptOffreModalOpen(true);
    }
  }, [bdcSignatureMode, findCommandeId, operationData]);

  const handleAccepterOffreAvecCGV = useCallback(async () => {
    const ok = await saveReferenceIfChanged();
    if (!ok) return;
    // Les CGV passent TOUJOURS avant la signature. On ne saute la popup que si elles
    // ont deja ete cochees dans cette session (un refresh les redemande donc).
    if (acceptCGV) {
      openSignatureOrAccept();
    } else {
      setReadyToAcceptOffreAfterCGV(true);
      setIsCGVModalOpen(true);
    }
  }, [saveReferenceIfChanged, acceptCGV, openSignatureOrAccept]);

  const handleAcceptOffre = useCallback(async () => {
    try {
      setLoading(true);
      const commandeId = findCommandeId();
      if (!operationData.id) throw new Error("ID de l'opération non trouvé");
      if (!commandeId) throw new Error(`ID de la commande du rang ${rang} non trouvé`);
      await accepterOffre(operationData.id, commandeId);
      window.location.reload();
    } catch (error) {
      setFeedback({ type: 'danger', message: error instanceof Error ? error.message : "Erreur lors de l'acceptation" });
    } finally {
      setLoading(false);
    }
  }, [findCommandeId, operationData, rang]);

  const handleRefuseOffre = useCallback(async () => {
    try {
      setLoading(true);
      const commandeId = findCommandeId();
      if (!operationData.id) throw new Error("ID de l'opération non trouvé");
      if (!commandeId) throw new Error(`ID de la commande du rang ${rang} non trouvé`);
      await refuserOffre(operationData.id, commandeId);
      window.location.reload();
    } catch (error) {
      setFeedback({ type: 'danger', message: error instanceof Error ? error.message : 'Erreur lors du refus' });
    } finally {
      setLoading(false);
    }
  }, [findCommandeId, operationData, rang]);

  const handleCancelOperation = useCallback(async () => {
    setIsDeleting(true);
    try {
      await annulerOperation(operationData.id);
      window.location.href = '/operations';
    } catch (error) {
      setFeedback({ type: 'danger', message: error instanceof Error ? error.message : "Erreur lors de l'annulation de l'opération." });
    } finally {
      setIsDeleting(false);
      setIsCancelModalOpen(false);
    }
  }, [operationData]);

  const handleDownloadBonCommande = useCallback(async () => {
    try {
      const blob = await telechargerBonCommande(operationData.numero ?? operationData.id);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      const rawNumero = String(operationData.numero ?? operationData.id ?? 'document');
      const numeroSansP = rawNumero.startsWith('P') ? rawNumero.substring(1) : rawNumero;
      const annee = process.env.REACT_APP_ANNEE_EDITION || '25';
      link.download = `${annee}${numeroSansP} (signed).pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch {
      setFeedback({ type: 'danger', message: 'Erreur lors du téléchargement du bon de commande. Veuillez réessayer.' });
    }
  }, [operationData]);

  const handleNotifyResponsable = useCallback(async () => {
    const ok = await saveReferenceIfChanged();
    if (!ok) return;
    setIsNotifyResponsableModalOpen(true);
    setResponsablesLoading(true);
    try {
      const list = await loadSignataires();
      setResponsables(list);
    } catch {
      setResponsables([]);
    } finally {
      setResponsablesLoading(false);
    }
  }, [saveReferenceIfChanged]);

  const handleConfirmNotifyResponsable = useCallback(async () => {
    if (!selectedResponsable) {
      setFeedback({ type: 'danger', message: 'Veuillez sélectionner un responsable.' });
      return;
    }
    try {
      const result = await notifierResponsable(
        operationData.id,
        selectedResponsable.id,
        selectedResponsable.type,
      );
      setFeedback({ type: 'success', message: `Demande envoyée à ${result.responsable?.prenom} ${result.responsable?.nom} avec succès.` });
      setIsNotifyResponsableModalOpen(false);
      setSelectedResponsable(null);
    } catch (error) {
      setFeedback({ type: 'danger', message: `Erreur lors de l'envoi de la demande au responsable : ${(error as Error).message}` });
    }
  }, [selectedResponsable, operationData]);

  // --- Helpers d'affichage ----------------------------------------------------
  const getDisplayStatus = (): string => operationData.status?.nom || 'Statut inconnu';
  const getSousTitre = (): string =>
    '';
  const getSubtitle = (): string => {
    const lotTechnique = operationData.operation?.lot_technique || operationData.lot_technique;
    const departement =
      operationData.operation?.lot_departement?.departement || operationData.lot_departement?.departement;
    if (lotTechnique && departement) {
      return `${lotTechnique.id} - ${lotTechnique.nom}, ${departement.id} - ${departement.nom}`;
    }
    return 'Informations de lot non disponibles';
  };

  const showActionRow =
    actions.annuler.visible ||
    actions.refuserOffre.visible ||
    actions.accepterOffre.visible ||
    actions.demanderValidation.visible;

  // Vue-modele du formulaire administratif (lecture seule).
  const formData: OperationFormulaire = {
    ...(operationData.formulaire || {}),
    nomOperation: operationData.nom || operationData.formulaire?.nomOperation || '',
    referenceInterne: operationData.reference || operationData.formulaire?.referenceInterne || '',
    adresse: operationData.adresse || operationData.formulaire?.adresse || '',
    codePostal: operationData.code_postal || operationData.formulaire?.codePostal || '',
    ville: operationData.ville || operationData.formulaire?.ville || '',
    coordonneesMaitriseDOuvrage:
      operationData.facturation || operationData.formulaire?.coordonneesMaitriseDOuvrage || '',
    coordonneesMaitrise:
      operationData.coordonnees_maitrise_ouvrage || operationData.formulaire?.coordonneesMaitrise || '',
    delaisConsultation: operationData.consultation || 10,
    delaisFinOperation:
      operationData.delai_execution || operationData.delais || operationData.formulaire?.delaisFinOperation || 30,
    visitePreavis: operationData.visite === true || operationData.visite === 1 || operationData.visite === '1',
    conditionVisite: operationData.condition_visite || operationData.formulaire?.conditionVisite || '',
    dateDebutOperation: operationData.date_debut_operation || operationData.formulaire?.dateDebutOperation || '',
    dateFinOperation: operationData.date_fin_operation || operationData.formulaire?.dateFinOperation || '',
  };

  return (
    <>
      <div className="consultation-bc">
        {feedback && <Banner variant={feedback.type} message={feedback.message} />}
        {refusBannerText && <Banner variant="warning" filled message={refusBannerText} />}
        {expired && (
          <div
            className="expired-notice"
            style={{
              backgroundColor: '#fee',
              border: '2px solid #c33',
              borderRadius: '8px',
              padding: '16px',
              marginBottom: '24px',
              color: '#c33',
              fontWeight: 'bold',
              textAlign: 'center',
            }}
          >
            {operationData.status?.id === StatusId.Annule
              ? "Cette commande a été annulée par l'acheteur. Vous ne pouvez plus effectuer d'actions."
              : 'Cette commande est expirée. Vous ne pouvez plus modifier votre offre ni effectuer d\'actions.'}
          </div>
        )}

        <AnalyseAccordion title="Descriptif Administratif" defaultOpen>
          <div className="section-content">
            <OperationFormulaireAdmin
              operation={operationData}
              formData={formData}
              viewMode
              errors={{}}
            />
          </div>
        </AnalyseAccordion>

        <AnalyseAccordion title="Descriptif Technique" defaultOpen>
          <div className="section-content">
            <div className="form-group">
              <label>
                Cahier des charges{' '}
                <span className="desc-precision">
                  (Les liens URL vers des pièces graphiques ou des documents complémentaires peuvent être
                  proposés par l’acheteur à titre d’information, mais seules les informations écrites ci-dessous
                  sont contractuelles. Par ailleurs, toutes précisions administratives ou juridiques de nature à
                  contrevenir à l’accord-cadre général sans dérogations prévues au contrat sont à considérer comme
                  inexistantes dans ce descriptif)
                </span>
              </label>
              <div className="input-inactif definition-besoin">
                {operationData.besoin || operationData.operation?.besoin || operationData.formulaire?.besoin || 'Non défini'}
              </div>
            </div>
          </div>
        </AnalyseAccordion>

        <AnalyseAccordion title="Descriptif Financier" defaultOpen>
          <div className="section-content">
            {articles.length > 0 ? (
              <div className="articles-table-container">
                <div className="articles-table-wrapper">
                  <table>
                    <thead>
                      <tr>
                        <th>N° Réf</th>
                        <th>Description de la prestation</th>
                        <th>Unité</th>
                        <th>Quantité</th>
                        <th>Prix unitaire HT</th>
                        <th>Total HT</th>
                      </tr>
                    </thead>
                    <tbody>
                      {articles.map((article, index) => (
                        <tr key={article.id || index}>
                          <td>{article.code}</td>
                          <td>
                            <div className="article-name">{article.nom}</div>
                          </td>
                          <td>{article.unite}</td>
                          <td>{article.quantite}</td>
                          <td>{formatPrice(article.prixUnitaire, 4, true)}</td>
                          <td>{formatPrice(article.totalHT, 4, true)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                <div className="articles-actions">
                  <div className="total-section">
                    <p>
                      <strong>
                        Total de l'offre (Rang {rang}) : {formatPrice(totalOffre, 4, true)} HT
                      </strong>
                    </p>
                  </div>
                </div>
              </div>
            ) : (
              <div className="no-articles-message">
                <p>Aucun article défini pour cette offre.</p>
              </div>
            )}
          </div>
        </AnalyseAccordion>

        <AnalyseAccordion title="Contact et documents" defaultOpen>
          <div className="section-content coordinates-section">
            <Coordonnees
              operation={operationData}
              prestataire={operationData.commandes?.[rang - 1]}
              isAcheteur
              isPresta={false}
              showSignataire={false}
            />
          </div>
        </AnalyseAccordion>

        {/* BCI : pas de section « Numéro de référence interne » dans l'onglet Offre. */}

        {offreState.showBonCommande && (
          <section className="bon-commande-section">
            <h3 className="section-header">Bon de Commande</h3>
            <div className="section-content">
              <div className="bon-commande-info">
                <div className="bon-commande-message">
                  <p>Le bon de commande a été généré et est disponible au téléchargement.</p>
                  <p>
                    <strong>
                      {(() => {
                        const cmd = operationData.commandes?.find(
                          (c) => parseInt(String(c.rang), 10) === rang,
                        );
                        const presta =
                          cmd?.contrat?.entite?.nom || operationData.prestataire?.nom || 'le prestataire';
                        const when = cmd?.updated_at
                          ? `${new Date(cmd.updated_at).toLocaleDateString('fr-FR')} à ${new Date(
                              cmd.updated_at,
                            ).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}`
                          : 'date non disponible';
                        return `Opération acceptée par ${presta} le ${when}.`;
                      })()}
                    </strong>
                  </p>
                </div>
                <div className="bon-commande-actions">
                  <Button
                    value="Télécharger le Bon de Commande"
                    variant="primary"
                    size="medium"
                    icon={<PdfIcon />}
                    onClick={handleDownloadBonCommande}
                  />
                </div>
              </div>
            </div>
          </section>
        )}

        {/* Refus / expiration / annulation : affiche en banniere (haut + bas) via refusBannerText. */}

        {/* {!operationData.transferred && statusFlags.isPending && (
          <div className="section-content consultation-info">
            <div className="info-header">
              <h4>En attente de l'acceptation du prestataire</h4>
              <p>Le prestataire de rang {rang} doit accepter ou refuser cette opération.</p>
              {operationData.expire_at && (
                <p>
                  <strong>Échéance dans :</strong> <CompteARebourd targetDate={operationData.expire_at} />
                </p>
              )}
            </div>
          </div>
        )} */}

        <div className="action-buttons-wrapper">
          {showActionRow && (
            <>
              <div className="action-buttons-row">
                {/* BCI : pas de bouton « Annuler la commande » (un seul bouton « Refuser l'offre » ;
                    la route /bdci/operation/{id}/annuler n'existe pas cote core). */}
                {actions.refuserOffre.visible && (
                  <Button
                    value="Refuser l'offre"
                    variant="secondary"
                    onClick={() => setIsRefuseOffreModalOpen(true)}
                    disabled={actions.refuserOffre.disabled || loading}
                  />
                )}
                {actions.accepterOffre.visible && (
                  <Button
                    value="Accepter l'offre"
                    variant="primary"
                    onClick={handleAccepterOffreAvecCGV}
                    disabled={actions.accepterOffre.disabled || loading}
                  />
                )}
                {actions.demanderValidation.visible && (
                  <Button
                    value="Obtenir la validation de l'acheteur signataire"
                    variant="primary"
                    onClick={handleNotifyResponsable}
                  />
                )}
              </div>
              {actions.demanderValidation.visible &&
                notificationInfo.sent &&
                notificationInfo.labels.length > 0 && (
                  <div className="notifications-sent-list">
                    {notificationInfo.labels.map((label, index) => (
                      <div key={index} className="notification-sent-info">
                        <span className="notification-sent-icon">✓</span>
                        <span>{label}</span>
                      </div>
                    ))}
                  </div>
                )}
            </>
          )}

          {statusFlags.isAccepted && <div className="status-message accepted">✓ Offre acceptée</div>}
          {/* Refus : affiche via la banniere d'etat (refusBannerText), plus de badge rouge « ✗ Offre refusée ». */}
          {statusFlags.isExpired && <div className="status-message expired">⏰ Offre expirée</div>}

          {printRef && (
            <div className="print-button-inline">
              <PrintButton
                targetRef={printRef}
                fileName={`${operationData.numero || operationData.operation?.numero} - Offre`}
                value="Imprimer l'offre"
                suiviHeaderProps={{
                  titre: operationData.operation?.nom || operationData.nom || '',
                  sousTitre: getSousTitre(),
                  subtitle: getSubtitle(),
                  status: getDisplayStatus(),
                  statusColor: getStatusColor(getDisplayStatus()),
                  delais: operationData.expire_at,
                  backgroundImage: lotImage,
                }}
              />
            </div>
          )}
        </div>

        {/* Rappel en bas de la banniere d'etat (refus/expiration/annulation). */}
        {refusBannerText && <Banner variant="warning" filled message={refusBannerText} />}

        {/* Modales */}
        <CGVAccept
          acceptCGV={acceptCGV}
          setAcceptCGV={setAcceptCGV}
          isOpen={isCGVModalOpen}
          onClose={() => {
            setIsCGVModalOpen(false);
            setReadyToAcceptOffreAfterCGV(false);
          }}
          onConfirm={() => {
            setAcceptCGV(true);
            setTimeout(() => {
              setIsCGVModalOpen(false);
              if (readyToAcceptOffreAfterCGV) {
                setReadyToAcceptOffreAfterCGV(false);
                openSignatureOrAccept();
              }
            }, 100);
          }}
        />

        <ValidationPopUp
          isOpen={isAcceptOffreModalOpen}
          title="Accepter l'offre"
          text={`Êtes-vous sûr de vouloir accepter cette offre pour un montant de ${formatPrice(
            totalOffre,
            4,
            true,
          )} HT ? Cette action est définitive.`}
          onClose={() => setIsAcceptOffreModalOpen(false)}
          onConfirm={async () => {
            setIsAcceptOffreModalOpen(false);
            await handleAcceptOffre();
          }}
          cancelLabel="Annuler"
          confirmLabel="Accepter l'offre"
        />

        <ValidationPopUp
          isOpen={isRefuseOffreModalOpen}
          title="Refuser l'offre"
          text="Êtes-vous sûr de vouloir refuser cette offre ?"
          onClose={() => setIsRefuseOffreModalOpen(false)}
          onConfirm={async () => {
            setIsRefuseOffreModalOpen(false);
            await handleRefuseOffre();
          }}
          cancelLabel="Annuler"
          confirmLabel="Refuser l'offre"
        />

        <ValidationPopUp
          isOpen={isCancelModalOpen}
          title="Annulation de la commande"
          text="Êtes-vous sûr de vouloir annuler cette commande ? L'opération et les commandes liées passeront en statut annulé. Cette action est irréversible."
          onClose={() => setIsCancelModalOpen(false)}
          onConfirm={handleCancelOperation}
          cancelLabel="Non"
          confirmLabel="Oui, annuler"
        />

        <ValidationPopUp
          isOpen={isNotifyResponsableModalOpen}
          title="Obtenir la validation de l'acheteur signataire"
          text=""
          onClose={() => {
            setIsNotifyResponsableModalOpen(false);
            setSelectedResponsable(null);
          }}
          onConfirm={handleConfirmNotifyResponsable}
          cancelLabel="Annuler"
          confirmLabel="Envoyer la demande"
        >
          <div className="mt-2">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Sélectionnez le responsable à qui envoyer la demande :
            </label>
            {responsablesLoading ? (
              <p className="text-gray-500">Chargement...</p>
            ) : responsables.length === 0 ? (
              <p className="text-red-500">Aucun responsable disponible</p>
            ) : (
              <select
                className="w-full p-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                value={selectedResponsable ? `${selectedResponsable.type}-${selectedResponsable.id}` : ''}
                onChange={(e) => {
                  const value = e.target.value;
                  if (value) {
                    const [type, id] = value.split('-');
                    setSelectedResponsable({ id: parseInt(id, 10), type });
                  } else {
                    setSelectedResponsable(null);
                  }
                }}
              >
                <option value="">-- Choisir un responsable --</option>
                {responsables.map((resp) => (
                  <option key={`${resp.type}-${resp.id}`} value={`${resp.type}-${resp.id}`}>
                    {resp.prenom} {resp.nom} -{' '}
                    {resp.type === 'responsable_achat' ? 'Acheteur signataire' : 'Validateur Achat'}
                    {resp.fonction ? ` (${resp.fonction})` : ''}
                  </option>
                ))}
              </select>
            )}
            {selectedResponsable && (
              <p className="mt-2 text-sm text-gray-600">
                Email :{' '}
                {
                  responsables.find(
                    (r) => r.id === selectedResponsable.id && r.type === selectedResponsable.type,
                  )?.email_contact
                }
              </p>
            )}
          </div>
        </ValidationPopUp>
      </div>

      {showBdcSignatureModal && pendingBdcSignData && (
        <SignatureFlow
          operationId={pendingBdcSignData.operationId}
          commandeId={pendingBdcSignData.commandeId}
          resourcePrefix="bdc"
          onClose={() => {
            setShowBdcSignatureModal(false);
            setPendingBdcSignData(null);
          }}
          onSigned={() => {
            setShowBdcSignatureModal(false);
            setPendingBdcSignData(null);
            window.location.reload();
          }}
        />
      )}
    </>
  );
};

export default OperationOffre;
